import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useEffect, useRef, useState } from 'react'
import { postMemoryQuery } from './memoryQuery'
import MemoryDocumentPanel from './MemoryDocumentPanel'
import type { MemoryCopy, MemoryDocumentDetail, MemoryDocumentsPayload, MemoryDocumentSummary, MemoryRow } from './types'

interface Props {
  workerId: string
  copy: MemoryCopy
  summary: MemoryDocumentSummary
  canManage: boolean
  onSelectMemory: (row: MemoryRow) => void
  onDeleted: () => void
  onClose: () => void
}

export default function MemoryDocumentDialog({ workerId, copy, summary, canManage, onSelectMemory, onDeleted, onClose }: Props) {
  const [detail, setDetail] = useState<MemoryDocumentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const requestRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    requestRef.current = controller
    setDetail(null)
    setError(null)
    setLoading(true)
    const load = async () => {
      try {
        const result = await postMemoryQuery<MemoryDocumentsPayload>(`/dashboard/digital_workers/${workerId}/memory_documents_search`, { document_id: summary.id, include_memories: true }, controller.signal)
        if (result.ok && result.data.document) setDetail(result.data.document)
        else setError(copy.documents.error)
      } catch (requestError) {
        if ((requestError as Error).name !== 'AbortError') setError(copy.documents.error)
      } finally {
        if (requestRef.current === controller) setLoading(false)
      }
    }
    void load()
    return () => controller.abort()
  }, [copy.documents.error, summary.id, workerId])

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      {/* max-w-3xl desde que a coluna lateral do documento saiu: sem ela, 4xl
          deixava a leitura larga demais. */}
      <DialogContent className="h-[min(820px,calc(100vh-2rem))] max-w-3xl gap-0 overflow-hidden bg-card p-0 sm:rounded-xl [&>button]:hidden">
        <DialogTitle className="sr-only">{copy.documents.detail_title}</DialogTitle>
        <MemoryDocumentPanel workerId={workerId} copy={copy} summary={summary} detail={detail} loading={loading} error={error} canManage={canManage && (Boolean(detail) || summary.upload_status === 'failed')} onSelectMemory={onSelectMemory} onDeleted={onDeleted} onClose={onClose} />
      </DialogContent>
    </Dialog>
  )
}
